import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useRequests } from '../requests/useRequests'
import { formatDateToISO } from '../utils/dateFormatter'

export interface BlogPostFormData {
  title: string
  description: string
  content: string
  image: string
  categories: string[]
  date: string
  slug: string
  published: boolean
}

const initialFormData: BlogPostFormData = {
  title: '',
  description: '',
  content: '',
  image: '',
  categories: [],
  date: formatDateToISO(new Date()),
  slug: '',
  published: true
}

export const useBlogPostForm = () => {
  const { id } = useParams<{ id: string }>()
  const isEditing = Boolean(id)
  const [formData, setFormData] = useState<BlogPostFormData>(initialFormData)
  const [loading, setLoading] = useState<boolean>(false)
  const [fetchLoading, setFetchLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const requests = useRequests()
  const navigate = useNavigate()

  const fetchBlogPost = async () => {
    if (!isEditing) return

    try {
      setFetchLoading(true)
      const response = await requests.getBlogPostById(Number(id))

      const postData = {
        ...response.data,
        date: formatDateToISO(new Date(response.data.date))
      }

      setFormData(postData)
    } catch (err) {
      setError('Nie udało się załadować artykułu do edycji.')
      console.error('Błąd podczas pobierania artykułu:', err)
    } finally {
      setFetchLoading(false)
    }
  }

  useEffect(() => {
    if (isEditing) {
      fetchBlogPost()
    }
  }, [id])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (name === 'published') {
      setFormData({ ...formData, published: (e.target as HTMLInputElement).checked })
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w\s]/gi, '')
      .replace(/\s+/g, '-')

    setFormData({ ...formData, slug })
  }

  const updateImage = (imageUrl: string) => {
    setFormData({
      ...formData,
      image: imageUrl
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title || !formData.description || !formData.content || !formData.image || !formData.slug) {
      setError('Proszę wypełnić wszystkie wymagane pola.')
      return
    }

    if (formData.categories.length === 0) {
      setError('Proszę dodać co najmniej jedną kategorię.')
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (isEditing) {
        await requests.updateBlogPost(Number(id), formData)
        setSuccess('Artykuł został zaktualizowany pomyślnie.')
      } else {
        await requests.createBlogPost(formData)
        setSuccess('Artykuł został utworzony pomyślnie.')
        setFormData(initialFormData)
      }

      setTimeout(() => {
        navigate('/blog')
      }, 2000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Wystąpił błąd podczas zapisywania artykułu.')
      console.error('Błąd podczas zapisywania artykułu:', err)
    } finally {
      setLoading(false)
    }
  }

  return {
    formData,
    setFormData,
    loading,
    fetchLoading,
    error,
    success,
    isEditing,
    handleChange,
    generateSlug,
    updateImage,
    handleSubmit,
    setError,
    setSuccess
  }
}
